// Copyright 2016 Sorama B.V.

import { DialogService } from "aurelia-dialog";
import { autoinject, BindingEngine, computedFrom, Disposable, observable, PLATFORM } from "aurelia-framework";
import { Router } from "aurelia-router";
import { AlertContent } from "controls/multi-alert/MultiAlert";
import { Corners } from "controls/plot/scan-planner/ScanPlanExtensions";
import { RecordingGroupMetadata } from "datatypes/Containers";
import { ScanArea, SubScanArea, UiRecordingStep } from "datatypes/ScanStep";
import { Point2D } from "datatypes/SoramaLocalApi";
import { IStorageService } from "services/Storage/IStorageService";

import { Point3D } from "datatypes/SoramaWebApi";
import { URA } from "libraries/Math/URA";
import { OrderedRecordingStepGroup, OrderedRecordingSteps } from "libraries/Scanning/OrderedRecordingSteps";
import { RecordingStepFactory } from "libraries/Scanning/RecordingStepsFactory";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ConfirmationModel } from "modules/dialogs/Confirmation/ConfirmationDialog";
import { MicrophoneArrayRackId } from "services/Hardware/Datatypes/MeasurementDevice";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { MotionDeviceService } from "services/MotionDevice/MotionDeviceService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IServerStatusService } from "services/Server/IServerStatusService";
import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";

/**
 * MODULE OPERATION DESCRIPTION - MeasureModule
 * The Measure Module provides functionality for performing measurements.
 *
 * Required Functionality:
 * * Generating steps, based on area of interest
 * * Execute commands to move array to specific position
 * * Trigger a measurement
 * * store a measurement
 *
 */
@autoinject
export class MeasureModule implements IWorkflowModuleLifetime {
    //#region Constants
    private postfixRegex = /\(([0-9]+)\)$/;
    private measureTimerUpdateInterval = 0.1;
    //#endregion

    //#region State Properties
    private recordingGroupContainerMetadata: RecordingGroupMetadata;

    private initCompleted: boolean;
    private measurementDeviceChanged: boolean;

    @observable private isMeasuring: boolean;
    private isCompleting: boolean;
    @observable private isCounting: boolean;
    private isMeasurementRegistered: boolean;
    private areRecordingsComplete: boolean;

    private orderedSteps: OrderedRecordingStepGroup[];

    @observable
    private currentStep: UiRecordingStep;

    @observable
    private currentStepGroup: OrderedRecordingStepGroup;

    private measureTimerHandle: number = null;
    private measureStartedAt: number;

    private isCancelled = false;
    private planModeActive = true;

    private measurementDeviceDimensions: Point2D;

    private timer: number;
    private selftimerAmount = 0;
    private countdownValue: number;

    //#endregion

    //#region Workflow Properties

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private recordingGroupContainerId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    private measurementName: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public measurementSetupId: string;

    // Measurement duration in [s]
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public measurementDuration: number;

    // Measurement duration in [m]
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public measurementDistance: number;

    // Scan Plan Settings
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public scanningArea: ScanArea;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public subScanArea: SubScanArea;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private isMeasurementComplete: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private motionDeviceOffset: Point3D;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementArea: "single" | "quick" | "custom";

    //#endregion

    //#region Other Properties
    private subscriptions: Disposable[];
    private moduleInstance: IWorkflowModuleInstance;

    @computedFrom(
        "measurementName",
        "projectManagementService.currentProject",
        "measurementDistance",
        "serverStatusService.online"
    )
    public get measurementAlerts(): AlertContent[] {
        const alerts = [
            {
                alertText: "Correct measurement setup",
                alertIcon: "fa-info-circle",
                alertSubText: "Place the Sound Camera " + this.measurementDistance + " m from the source",
                alertType: "alert-info",
                secondaryIcon: "",
                alertLink: false,
            },
        ];

        if (!this.measurementName) {
            alerts.push({
                alertText: "Measurement Name",
                alertIcon: "fa-exclamation-triangle",
                alertSubText: "Please enter a measurement name",
                alertType: "alert-danger",
                secondaryIcon: "",
                alertLink: false,
            });
        }
        if (
            this.serverStatusService.online.available &&
            (!this.projectManagementService.currentProject.item ||
                !this.projectManagementService.currentProject.status.available)
        ) {
            alerts.push({
                alertText: "No project selected",
                alertIcon: "fa-exclamation-triangle",
                alertSubText: "",
                alertType: "alert-danger",
                secondaryIcon: "",
                alertLink: true,
            });
        }

        return alerts;
    }

    @computedFrom(
        "isMeasuring",
        "measurementDeviceChanged",
        "!measurementAcquisitionService.isReserved",
        "measurementAlerts"
    )
    public get canMeasure(): boolean {
        // Check state variables
        if (
            this.isMeasuring ||
            this.measurementDeviceChanged ||
            !this.measurementAcquisitionService.isReserved.available
        ) {
            return false;
        }

        // Check if there are any alerts with 'danger'
        if (this.measurementAlerts.some((x) => x.alertType === "alert-danger")) {
            return false;
        }

        return true;
    }
    //#endregion

    constructor(
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly storageService: IStorageService,
        private readonly dialogService: DialogService,
        private readonly recordingGroupContainerStorageService: IRecordingGroupContainerStorageService,
        private readonly projectManagementService: IProjectManagementService,
        private readonly bindingEngine: BindingEngine,
        private readonly serverStatusService: IServerStatusService,
        protected readonly router: Router,
        private readonly motionService: MotionDeviceService,
        private readonly recordingStepFactory: RecordingStepFactory
    ) {
        this.serverStatusService = serverStatusService;
    }

    //#region Lifecycle functions
    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;
        this.initCompleted = false;

        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "_activeSetup")
                .subscribe(this.onSelectedSetupPropertiesChanged),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "_channelSelection")
                .subscribe(this.onSelectedSetupPropertiesChanged),
        ];

        this.loadRecordingGroupContainer();
        this.onSelectedSetupPropertiesChanged();
    }

    public deactivateModule(): void {
        while (this.subscriptions.length) this.subscriptions.pop().dispose();
    }
    //#endregion

    //#region Property Changed Methods
    private currentStepGroupChanged(): void {
        if (!this.currentStepGroup || this.currentStepGroup.recordingSteps.length == 0) this.currentStep = undefined;

        if (
            this.currentStepGroup &&
            this.currentStepGroup.recordingSteps.findIndex((x) => x == this.currentStep) === -1
        ) {
            const idx = this.currentStepGroup.recordingSteps.findIndex((x) => !!x.recordingId);
            this.currentStep = this.currentStepGroup.recordingSteps[idx == -1 ? 0 : idx];
        }
    }

    private scanningAreaChanged(): void {
        this.updateSteps();
    }

    private subScanAreaChanged(): void {
        this.updateSteps();
    }

    private onSelectedSetupPropertiesChanged = () => {
        // Check if stuff works
        const hasMeasurementSetupChanged =
            this.measurementSetupId &&
            (!this.measurementAcquisitionService.activeSetup ||
                this.measurementAcquisitionService.activeSetup.id != this.measurementSetupId);

        if (
            this.orderedSteps &&
            this.orderedSteps.some((x) => x.recordingSteps.some((y) => !!y.recordingId)) &&
            hasMeasurementSetupChanged
        ) {
            this.measurementDeviceChanged = true;
        } else {
            this.measurementDeviceChanged = false;

            if (this.measurementAcquisitionService.activeSetup) {
                // Get dimensions of the physical array
                const uraProperties = URA.getUraProperties3D(
                    this.measurementAcquisitionService.activeSetup.sensorPositions.map(
                        (x) => <Point3D>{ x: x.X, y: x.Y, z: x.Z }
                    )
                );

                if (uraProperties.isUra) {
                    this.measurementDeviceDimensions = { x: uraProperties.width, y: uraProperties.height };
                } else {
                    // Just use min and max of arrays, without taking the edges into account. This is not correct for sunflower array.
                    let pos = this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => x.Y);
                    const height = Math.max(...pos) - Math.min(...pos);
                    pos = this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => x.X);
                    const width = Math.max(...pos) - Math.min(...pos);
                    this.measurementDeviceDimensions = { x: width, y: height };
                }

                if (!this.orderedSteps) {
                    this.updateSteps();
                }
            }
        }

        this.setModuleState();
    };

    private loadRecordingGroupContainer = () => {
        if (!this.recordingGroupContainerId) return;

        this.storageService
            .getMetadata<RecordingGroupMetadata>(this.recordingGroupContainerId)
            .then((x) => (this.recordingGroupContainerMetadata = x));

        // Make sure we have selected the correct device
        this.onSelectedSetupPropertiesChanged();
        this.initCompleted = true;
        this.currentStep = undefined;

        this.setModuleState();
        this.setNextRecordingStep();
    };

    public updateSteps(): void {
        if (!this.scanningArea) return;

        let orderedSteps: OrderedRecordingSteps;

        // Update steps on disk or in planner if necessary
        if (
            this.recordingGroupContainerMetadata &&
            this.recordingGroupContainerMetadata.steps &&
            this.recordingGroupContainerMetadata.steps.some((x) => !!x.recordingId)
        ) {
            // Update planner steps with data from disk
            orderedSteps = new OrderedRecordingSteps(
                this.recordingGroupContainerMetadata.steps,
                this.scanningArea,
                Corners.TopLeft,
                "x"
            );
        } else if (this.measurementAcquisitionService.activeSetup && this.measurementDeviceDimensions) {
            // Generate new steps
            orderedSteps = this.recordingStepFactory.createOrderedRecordingSteps(
                this.scanningArea,
                this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => {
                    return { x: x.X, y: x.Y, z: x.Z };
                }),
                this.measurementDeviceDimensions,
                this.measurementAcquisitionService.activeSetup.channelSelection,
                this.subScanArea
            );
        }

        if (orderedSteps) {
            this.orderedSteps = orderedSteps.splitStepsInGlobalSteps(this.measurementDeviceDimensions);
            this.currentStep = undefined;
            this.setNextRecordingStep();
        }
    }

    public recordingGroupContainerIdChanged(newValue: string, oldValue: string): void {
        // Container changed while acquisition is active, so stop.
        if (this.measurementAcquisitionService.isAcquiring.available) {
            clearInterval(this.measureTimerHandle);
            this.measurementAcquisitionService.stopAcquisition().then(() => {
                this.currentStep.measureProgress = this.currentStep.recordingId ? 1 : 0;
                this.currentStep.dataProgress = this.currentStep.recordingId ? 1 : 0;
                this.loadRecordingGroupContainer();
            });
        } else {
            this.loadRecordingGroupContainer();
        }
    }

    //#endregion

    //#region View related functions
    public doMeasurement(step?: UiRecordingStep): Promise<void> {
        this.isMeasuring = true;
        this.isCancelled = false;

        // We're doing a measurement, so set the scan-planner to scan mode
        this.planModeActive = false;

        if (step) this.currentStep = step;

        if (!this.currentStep) return Promise.resolve(); // No steps left

        this.measureTimerHandle = null;
        this.currentStep.measureProgress = 0;
        this.currentStep.dataProgress = 0;
        this.currentStep.stepError = null;
        this.currentStep.recordingId = undefined;

        let oldRecordingId: string = this.currentStep.recordingId;
        let currentRecordingId: string;

        return this.recordingGroupContainerStorageService
            .addRecording(this.recordingGroupContainerId)
            .then((recordingId) => {
                currentRecordingId = recordingId;

                // Start acquisition
                this.measurementAcquisitionService.setLowBandwidthMode(false);
                this.measurementAcquisitionService.setUseReferenceMicrophone(
                    this.orderedSteps.length > 1 || this.orderedSteps.some((x) => x.recordingSteps.length > 1)
                );
                this.measurementAcquisitionService.startAcquisition();

                // Record, is automatically started by the measurementAcquisitionService internals after startAcquisition is done.
                return this.measurementAcquisitionService.record(
                    this.measurementDuration,
                    recordingId,
                    this.soundProgressCallback
                );
            })
            .then((recordingId) => {
                // Save the current step
                this.currentStep.recordingId = recordingId;
                const steps = [].concat(
                    ...this.orderedSteps.map((x) => x.recordingSteps.map((y) => UiRecordingStep.toRecordingStep(y)))
                );
                steps.forEach((x, i) => (x.stepIndex = i));

                return this.recordingGroupContainerStorageService
                    .updateKnownMetadata(this.recordingGroupContainerId, "steps", steps)
                    .then(() => {
                        const microphoneArrayRacks: MicrophoneArrayRackId = {};

                        for (const device of this.measurementAcquisitionService.activeSetup) {
                            for (const rackId in device.measurementDevice.microphoneArrayRackIds) {
                                if (
                                    Object.prototype.hasOwnProperty.call(
                                        device.measurementDevice.microphoneArrayRackIds,
                                        rackId
                                    )
                                ) {
                                    microphoneArrayRacks[rackId] =
                                        device.measurementDevice.microphoneArrayRackIds[rackId];
                                }
                            }
                        }

                        this.storageService.updateFileMetadata(
                            recordingId,
                            "sound",
                            "microphoneArrayRacks",
                            microphoneArrayRacks
                        );
                    });
            })
            .catch((e: Error) => {
                if (!this.isCancelled) {
                    const errorMessage =
                        this.measurementAcquisitionService.isAcquiring.notAvailable &&
                        this.measurementAcquisitionService.isAcquiring.reason
                            ? this.measurementAcquisitionService.isAcquiring.reason
                            : e.message;

                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(errorMessage)
                        .show();

                    this.currentStep.stepError = errorMessage;
                }

                const promises: Promise<void>[] = [];
                this.currentStep.recordingId = oldRecordingId;
                if (oldRecordingId) {
                    // Make sure metadata is correct and delete the new recording we made
                    promises.push(
                        this.recordingGroupContainerStorageService
                            .updateKnownMetadata(
                                this.recordingGroupContainerId,
                                "steps",
                                [].concat(
                                    ...this.orderedSteps.map((x) =>
                                        x.recordingSteps.map((y) => UiRecordingStep.toRecordingStep(y))
                                    )
                                )
                            )
                            .catch(() => {})
                            .then(() => (oldRecordingId = undefined))
                    );
                }

                if (currentRecordingId) {
                    // Delete the new recording we made
                    promises.push(
                        this.recordingGroupContainerStorageService
                            .deleteRecording(this.recordingGroupContainerId, currentRecordingId)
                            .catch(() => {})
                    );
                }

                return Promise.all(promises);
            })
            .then(() => {
                // Delete old recording if any
                const deletePromise = oldRecordingId
                    ? this.recordingGroupContainerStorageService
                          .deleteRecording(this.recordingGroupContainerId, oldRecordingId)
                          .catch(() => {})
                    : Promise.resolve();

                // Make sure progress indicators show correct final value before changing currentStep
                this.currentStep.measureProgress = this.currentStep.recordingId ? 1 : 0;
                this.currentStep.dataProgress = this.currentStep.recordingId ? 1 : 0;

                this.setNextRecordingStep();

                return deletePromise;
            })
            .catch(() => {})
            .then(() => {
                clearInterval(this.measureTimerHandle);
                return this.measurementAcquisitionService.stopAcquisition().catch(() => {});
            })
            .then(() => {
                this.isMeasuring = false;

                this.moduleInstance.createTimelineEvent();
            });
    }

    private setNextRecordingStep(): void {
        if (!this.orderedSteps) return;

        const steps = [].concat(...this.orderedSteps.map((x) => x.recordingSteps));
        const currentIdx = steps.findIndex((x) => x == this.currentStep);

        let nextStep =
            currentIdx > steps.length
                ? undefined
                : steps.find((x) => !x.recordingId && (!this.currentStep || x.stepIndex > this.currentStep.stepIndex));

        if (!nextStep) {
            // If there is no further step, check if there is a step that is not finished yet
            nextStep = steps.find((x) => !x.recordingId);

            // If all steps are completed, move to the first step
            if (!nextStep && this.motionService.connectedMotionDevice) {
                this.moveToStep(this.orderedSteps[0].recordingSteps[0]);
            }
        }

        this.areRecordingsComplete = !nextStep;

        this.selectRecordingStep(
            this.orderedSteps.find((x) => !!x.recordingSteps.find((y) => y === nextStep)),
            nextStep
        );
    }

    public moveToStep(step: UiRecordingStep): Promise<void> {
        // Only send commands when required
        if (!this.motionService.connectedMotionDevice || this.selectedMeasurementArea === "single")
            return Promise.resolve();

        const stepToMove = step ? step : this.currentStep;

        if (!stepToMove) return Promise.resolve();

        const position = stepToMove.position;

        const pos: Point3D = {
            // change to MotionDevice coordinate system
            x: position.X + this.motionDeviceOffset.x,
            y: this.scanningArea.height - position.Y - this.measurementDeviceDimensions.y + this.motionDeviceOffset.y,
            z: -(position.Z + this.motionDeviceOffset.z),
        };

        return this.motionService.moveTo(pos);
    }

    private soundProgressCallback = (progress: number) => {
        // Set the measure timer as soon as the first progress report is in
        if (this.measureTimerHandle == null) {
            this.measureStartedAt = Date.now();
            this.measureTimerHandle = window.setInterval(() => {
                const elapsedTime = Date.now() - this.measureStartedAt;
                this.currentStep.measureProgress = elapsedTime / (this.measurementDuration * 1000);

                if (this.currentStep.measureProgress >= 1) {
                    this.currentStep.measureProgress = 1;
                    clearInterval(this.measureTimerHandle);
                }
            }, this.measureTimerUpdateInterval * 1000);
        }

        this.currentStep.dataProgress = progress;
    };

    public completeMeasurement(): void {
        this.isCompleting = true;
        this.projectManagementService
            .createMeasurement(this.recordingGroupContainerId)
            .then(() => {
                this.isMeasurementRegistered = true;
            })
            .catch(() => {
                // We are either offline or unable to complete the measurement, notify user what to do
                this.isMeasurementRegistered = false;
            })
            .then(() => {
                this.isMeasurementComplete = true;
                this.isCompleting = false;
            });
    }

    public cancelMeasurement(): void {
        if (this.motionService.connectedMotionDevice) this.motionService.stop();

        if (this.measurementAcquisitionService.isAcquiring.available || this.isCounting) {
            this.isCancelled = true;
            this.isCounting = false;
            clearTimeout(this.timer);
            this.timer = null;

            clearInterval(this.measureTimerHandle);
            this.measurementAcquisitionService.stopAcquisition().then(() => {
                this.currentStep.measureProgress = this.currentStep.recordingId ? 1 : 0;
                this.currentStep.dataProgress = this.currentStep.recordingId ? 1 : 0;
            });
        }

        if (
            this.measurementAcquisitionService.isAcquiring.notAvailable &&
            !this.isCounting &&
            this.measureStartedAt != undefined
        ) {
            this.deleteMeasurement();
        }
    }

    public newMeasurement(noIncrement = false): void {
        // Start by loading the latest recording group metadata from disk (other modules might have changed it since now)
        const metadataPromise = !this.recordingGroupContainerId
            ? Promise.resolve(this.recordingGroupContainerMetadata)
            : this.storageService
                  .getMetadata<RecordingGroupMetadata>(this.recordingGroupContainerId)
                  .catch(() => this.recordingGroupContainerMetadata);

        metadataPromise.then((oldContainerMetadata) => {
            let name =
                oldContainerMetadata && oldContainerMetadata.name
                    ? oldContainerMetadata.name.trim()
                    : this.measurementName
                      ? this.measurementName
                      : "";

            if (!noIncrement) {
                const regExResult = this.postfixRegex.exec(name);
                if (regExResult) {
                    const nextPostfix = Number.parseInt(regExResult[1]) + 1;
                    name = name.slice(0, regExResult.index) + "(" + nextPostfix + ")";
                } else {
                    name = name + " (1)";
                }
            }

            // Create a new measurement with the current settings
            return this.recordingGroupContainerStorageService
                .create()
                .then((newRecordingGroupContainer) => {
                    // Replicate data from old measurement
                    return this.recordingGroupContainerStorageService
                        .updateKnownMetadataSet(newRecordingGroupContainer.id, {
                            name: name,
                            notes: oldContainerMetadata.notes,
                            distance: oldContainerMetadata.distance,
                            metadata: oldContainerMetadata.metadata,
                        })
                        .then(() => newRecordingGroupContainer);
                })
                .then((newRecordingGroupContainer) => {
                    // "Clear" steps without updating them on the disk
                    this.orderedSteps.forEach((x) => x.recordingSteps.forEach((y) => UiRecordingStep.clear(y)));

                    // Set the new ID, this will trigger a reload from disk in this and other modules and will also update the steps
                    this.recordingGroupContainerId = newRecordingGroupContainer.id;
                })
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(e.message);
                })
                .then(() => {
                    this.loadRecordingGroupContainer();
                    this.onSelectedSetupPropertiesChanged();
                });
        });
    }

    public deleteMeasurement(): void {
        const confirmationModel: ConfirmationModel = {
            title: "Delete Measurement",
            text: "Are you sure you want to delete this measurement? This will remove all data from your hard disk!",
            confirmText: "Delete",
            operation: async () => {
                // Before deleting the measurement, make sure the current metadata is up to date
                const metadataPromise = this.recordingGroupContainerId
                    ? this.storageService
                          .getMetadata<RecordingGroupMetadata>(this.recordingGroupContainerId)
                          .catch(() => this.recordingGroupContainerMetadata)
                    : Promise.resolve(this.recordingGroupContainerMetadata);

                metadataPromise.then((metadata) => {
                    // Now we have cached the metadata of the old measurement, delete it and create a new measurement with the same settings
                    this.recordingGroupContainerMetadata = metadata;
                    return this.recordingGroupContainerStorageService
                        .delete(this.recordingGroupContainerId)
                        .catch(() => {})
                        .then(() => {
                            this.measureStartedAt = undefined;
                            this.recordingGroupContainerId = "";
                            this.newMeasurement(true);
                        });
                });
            },
        };

        this.dialogService.open({
            viewModel: PLATFORM.moduleName("modules/dialogs/Confirmation/ConfirmationDialog"),
            model: confirmationModel,
            lock: true,
        });
    }

    //#endregion

    //#region Helper Methods
    private setModuleState(): void {
        // If there is no measurement done yet, enable plan mode
        this.planModeActive = !this.orderedSteps
            ? true
            : !this.orderedSteps.find((x) => !!x.recordingSteps.find((y) => !!y.recordingId)) && !this.isMeasuring;
        this.areRecordingsComplete = !this.orderedSteps
            ? false
            : this.orderedSteps.every((x) => x.recordingSteps.every((y) => !!y.recordingId));

        this.isMeasurementRegistered = this.isMeasurementComplete = !this.recordingGroupContainerMetadata
            ? false
            : this.recordingGroupContainerMetadata.measurementIds &&
              this.recordingGroupContainerMetadata.measurementIds.length > 0;
    }

    private selectRecordingStep(step: OrderedRecordingStepGroup, recordStep: UiRecordingStep) {
        this.currentStep = recordStep;
        this.currentStepGroup = step;
    }

    //#endregion

    //#region Measurement Trigger

    private startMeasureCycle(step?: UiRecordingStep): Promise<void> {
        if (this.planModeActive && this.motionService.connectedMotionDevice) {
            // it is a scana measurement and the first step, so store the current offset
            this.motionDeviceOffset = {
                x: this.scanningArea.offsetX,
                y: this.scanningArea.offsetY,
                z: this.scanningArea.offsetZ,
            };
        }

        return this.moveToStep(step).then(() => {
            return this.countDown(this.selftimerAmount, step).then(() => {
                // When using a ScanA, move automatically to nex step
                if (this.motionService.connectedMotionDevice && !this.areRecordingsComplete)
                    return this.startMeasureCycle();
            });
        });
    }

    private countDown(countdownValue: number, step?: UiRecordingStep): Promise<void> {
        this.isCounting = true;
        this.countdownValue = countdownValue;

        if (this.countdownValue <= 0) {
            if (this.timer) clearTimeout(this.timer);

            this.timer = null;
            this.isCounting = false;

            return this.doMeasurement(step);
        } else {
            return new Promise((resolve, reject) => {
                this.timer = window.setTimeout(() => {
                    resolve(this.countDown(countdownValue - 1, step));
                }, 1000);
            });
        }
    }

    private changeSelftimerAmount(value?: number) {
        if (value || value == 0) {
            this.selftimerAmount = value;
        }
        document.getElementById("selftimerDropdown").classList.toggle("show");
    }
    //#endregion
}
